import React, { useMemo, useState } from "react"
import { graphql } from "gatsby"
import {
  Intro,
  Block,
  Grid,
  Container,
  Seo,
  Filter,
  VideoTile
} from "@components"
import Layout from "@layouts/layout"
import PropTypes from "prop-types"

// Category is a free text field in the CMS, so values are grouped and matched
// on a normalised form to survive stray capitals and whitespace.
const normalise = value =>
  (value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase()

const VideoList = ({ data, pageContext }) => {
  const { title = "Videos" } = pageContext
  const videos = data.videos.nodes

  const [search, setSearch] = useState("")
  const [category, setCategory] = useState("")

  const introConfig = {
    breadcrumbs: pageContext.breadcrumbs,
    title
  }

  // Build the category list from the videos themselves, keeping the first
  // spelling seen as the label so editors control the casing shown.
  const categoryOptions = useMemo(() => {
    const labels = new Map()

    videos.forEach(node => {
      const value = normalise(node.category)
      if (!value || labels.has(value)) return
      labels.set(value, node.category.trim().replace(/\s+/g, " "))
    })

    return [...labels]
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([value, text]) => ({ value, text }))
  }, [videos])

  const filteredVideos = useMemo(() => {
    const terms = normalise(search)
      .split(" ")
      .filter(Boolean)

    return videos.filter(node => {
      if (category && normalise(node.category) !== category) return false
      if (terms.length < 1) return true

      const haystack = normalise(
        [node.videoTitle, node.title, node.description, node.category]
          .filter(Boolean)
          .join(" ")
      )

      return terms.every(term => haystack.includes(term))
    })
  }, [videos, search, category])

  const updateFilters = ({ target }) => {
    if (target.name === "category") setCategory(target.value)
    if (target.name === "search") setSearch(target.value)
  }

  const clearFilters = () => {
    setCategory("")
    setSearch("")
  }

  const filters =
    categoryOptions.length > 0
      ? [
          {
            name: "category",
            label: "Category",
            value: category,
            options: categoryOptions,
            placeholder: {
              value: "",
              text: "Select category"
            }
          }
        ]
      : []

  const searchConfig = {
    name: "search",
    label: "Search videos",
    placeholder: "Search videos",
    value: search
  }

  return (
    <Layout>
      <Seo title={title} type={"website"} />
      <Block gutters>
        <Container>
          <Intro {...introConfig} />
        </Container>
      </Block>
      {videos.length > 0 && (
        <Block gutters>
          <Container>
            <form onSubmit={e => e.preventDefault()}>
              <Filter
                onClick={clearFilters}
                onChange={updateFilters}
                filters={filters}
                search={searchConfig}
              />
            </form>
          </Container>
        </Block>
      )}
      {filteredVideos.length > 0 ? (
        <Block padding="double-top-single-bottom">
          <Grid moduleStyle={"3-up-grid"}>
            {filteredVideos.map(node => (
              <VideoTile
                key={`video-tile-${node.id}`}
                heading={node.videoTitle || node.title}
                path={`/videos/${node.slug}/`}
                thumbnailUrl={node.video?.thumbnailUrl}
                duration={node.duration}
              />
            ))}
          </Grid>
        </Block>
      ) : (
        <Block padding="double" gutters>
          <center>
            Sorry, we can't find an exact match for your search. Please try
            again.
          </center>
        </Block>
      )}
    </Layout>
  )
}

VideoList.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object
}

export default VideoList

export const query = graphql`
  query videoListPage($language: String!) {
    videos: allDatoCmsVideoPage(
      locale: $language
      sort: { meta: { createdAt: DESC } }
    ) {
      nodes {
        id
        title
        slug
        videoTitle
        duration
        category
        description
        video {
          thumbnailUrl
        }
      }
    }
  }
`
